import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeScanGuard } from '../scanGuard';
import { scanSound } from '../scanSound';
import { productsText } from './productsText';

// The phone camera as a barcode scanner (Fernando 31.10, docs/PRODUCT-SCAN.md). Two readers behind one component:
// the browser's own BarcodeDetector where it exists (Chrome on Android — fast, no download), else zxing-wasm loaded
// only when the camera opens (iPhone Safari and the installed home-screen app; the .wasm is served from OUR origin,
// public/zxing/, never a CDN). Every read goes to `onScan(code)` — the same handler the keyboard-wedge scanner and
// the typed box feed. `continuous` keeps the camera open between reads (the move: one piece after another; a code
// is counted once per 1.5 s so a piece held still is not counted twice); otherwise the first read closes it.
// iOS: the video must be `playsInline muted`, the stream dies when the app goes to the background (restarted on
// return), no torch. -- Claude, 2026-10-31

type Props = { onScan: (code: string) => void; onClose: () => void; continuous?: boolean; lang: string; testMode?: boolean };

// 1D barcodes ONLY (Fernando 31.10: the physical scanners had QR, Aztec, DataMatrix, MaxiCode and Han Xin switched off
// the day before — double-marked products made every second read a DataMatrix). The camera reads what a bag label,
// a hang tag and a section label carry: EAN/UPC, Code128, ITF, Code39.
const NATIVE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'itf', 'code_39'];
const WASM_FORMATS = ['EAN13', 'EAN8', 'UPCA', 'UPCE', 'Code128', 'ITF', 'Code39'] as const;
const FRAME_MS = 150;
const CONTINUOUS_GUARD_MS = 1500;

export type Reader = { kind: 'native' | 'wasm'; read: (video: HTMLVideoElement) => Promise<string | null> };

/** which reader this browser gets; the wasm one is imported only now */
export async function makeReader(): Promise<Reader> {
  if (typeof (window as any).BarcodeDetector !== 'undefined') {
    try {
      const supported: string[] = await BarcodeDetector.getSupportedFormats();
      const formats = NATIVE_FORMATS.filter(f => supported.includes(f));
      if (formats.includes('ean_13') && formats.includes('code_128')) {
        const det = new BarcodeDetector({ formats });
        return { kind: 'native', read: async v => { const r = await det.detect(v); return r[0]?.rawValue || null; } };
      }
    } catch { /* fall through to wasm */ }
  }
  const z = await import('zxing-wasm/reader');
  z.prepareZXingModule({ overrides: { locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? '/zxing/zxing_reader.wasm' : prefix + path) } });
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true } as any) as CanvasRenderingContext2D | null;
  return {
    kind: 'wasm',
    read: async v => {
      if (!ctx || !v.videoWidth) return null;
      // a frame no wider than 800 px keeps the decode under ~100 ms on a phone; barcodes read fine at that size
      const scale = Math.min(1, 800 / v.videoWidth);
      canvas.width = Math.round(v.videoWidth * scale); canvas.height = Math.round(v.videoHeight * scale);
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const res = await z.readBarcodes(img, { formats: [...WASM_FORMATS], maxNumberOfSymbols: 1 });
      const hit = res.find(r => r.isValid && r.text);
      return hit ? hit.text : null;
    },
  };
}

export default function CameraScanner({ onScan, onClose, continuous = false, lang, testMode = false }: Props) {
  const t = productsText(lang);
  // the parent's callbacks in a ref: a parent re-render must not restart the camera (the effect below depends on
  // start/loop, which would otherwise change identity with every new onScan/onClose)
  const cb = useRef({ onScan, onClose });
  cb.current = { onScan, onClose };
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const readerRef = useRef<Reader | null>(null);
  const timer = useRef<number | null>(null);
  const alive = useRef(true);
  const guard = useRef(makeScanGuard(CONTINUOUS_GUARD_MS));
  const [state, setState] = useState<'starting' | 'live' | 'error' | 'tap'>('starting');
  const [msg, setMsg] = useState('');
  const [torch, setTorch] = useState<boolean | null>(null);     // null = no torch on this camera
  const [flash, setFlash] = useState(false);
  const [last, setLast] = useState<string[]>([]);                // test mode: what was read

  const stop = useCallback(() => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    streamRef.current?.getTracks().forEach(tr => tr.stop());
    streamRef.current = null;
  }, []);

  const loop = useCallback(() => {
    if (!alive.current) return;
    timer.current = window.setTimeout(async () => {
      const v = videoRef.current;
      const r = readerRef.current;
      if (v && r && v.readyState >= 2) {
        let code: string | null = null;
        try { code = await r.read(v); } catch { code = null; }
        if (code && alive.current) {
          if (testMode) {
            if (!guard.current(code)) { setLast(l => [code!, ...l].slice(0, 8)); scanSound('ok'); setFlash(true); setTimeout(() => setFlash(false), 150); }
          } else if (continuous) {
            if (!guard.current(code)) {
              scanSound('ok'); try { navigator.vibrate?.(60); } catch { /* iOS has none */ }
              setFlash(true); setTimeout(() => setFlash(false), 150);
              cb.current.onScan(code);
            }
          } else {
            scanSound('ok'); try { navigator.vibrate?.(60); } catch { /* iOS has none */ }
            stop(); cb.current.onScan(code); cb.current.onClose();
            return;
          }
        }
      }
      loop();
    }, FRAME_MS);
  }, [continuous, stop, testMode]);

  const start = useCallback(async () => {
    setState('starting'); setMsg('');
    if (!navigator.mediaDevices?.getUserMedia) { setState('error'); setMsg(t.cameraNo); return; }
    try {
      if (!readerRef.current) readerRef.current = await makeReader();
    } catch (e) {
      setState('error'); setMsg(t.cameraNo); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      if (!alive.current) { stream.getTracks().forEach(tr => tr.stop()); return; }
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        try { await v.play(); } catch { setState('tap'); return; }   // iOS without a gesture: ask for a tap
      }
      const track = stream.getVideoTracks()[0];
      const caps: any = track.getCapabilities ? track.getCapabilities() : {};
      setTorch(caps && caps.torch ? false : null);
      setState('live');
      loop();
    } catch (e: any) {
      setState('error');
      const name = e?.name || '';
      setMsg(name === 'NotAllowedError' || name === 'SecurityError' ? t.cameraDenied
        : name === 'NotFoundError' || name === 'OverconstrainedError' ? t.cameraNone : t.cameraNo);
    }
  }, [loop, t.cameraDenied, t.cameraNo, t.cameraNone]);

  useEffect(() => {
    alive.current = true;
    start();
    const onVis = () => { if (document.hidden) stop(); else if (alive.current) start(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive.current = false; document.removeEventListener('visibilitychange', onVis); stop(); };
  }, [start, stop]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || torch === null) return;
    try { await (track as any).applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { /* not supported after all */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col">
      <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-cover" />
      {/* the aiming frame; the rest dimmed */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute left-[8%] right-[8%] top-[30%] h-[28%] rounded-xl border-4 ${flash ? 'border-green-400 bg-green-400/20' : 'border-white/90'}`} />
      </div>
      <div className="relative flex items-center justify-between p-3 bg-black/50">
        <span className="text-sm">{state === 'live' ? (readerRef.current?.kind === 'native' ? t.camera : `${t.camera} · zxing`) : state === 'starting' ? t.cameraStarting : ''}</span>
        <div className="flex gap-2">
          {torch !== null && state === 'live' && (
            <button onClick={toggleTorch} className={`px-3 py-1.5 rounded-lg text-sm ${torch ? 'bg-amber-400 text-black' : 'bg-white/20'}`}>{t.torch}</button>
          )}
          <button onClick={() => { stop(); cb.current.onClose(); }} className="px-3 py-1.5 rounded-lg text-sm bg-white/20">{t.cameraStop}</button>
        </div>
      </div>
      <div className="relative mt-auto p-4 bg-black/50 text-center">
        {state === 'tap' && <button onClick={() => { videoRef.current?.play().then(() => { setState('live'); loop(); }).catch(() => setState('tap')); }} className="px-5 py-3 rounded-xl bg-white text-black font-semibold">{t.cameraStart}</button>}
        {state === 'error' && <div className="text-base">{msg}</div>}
        {state === 'live' && !testMode && <div className="text-sm opacity-80">{continuous ? t.scanning : t.hint}</div>}
        {testMode && (
          <div className="text-left text-sm font-mono">
            <div className="opacity-70 mb-1">camera test — nothing is looked up</div>
            {last.length === 0 ? <div>…</div> : last.map((c, i) => <div key={i}>{c}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
